/* =================================================================
   Modelo de datos central del Análisis de Valor (fuente de la verdad).
   Obligatorio/opcional según el brief §42.
   ================================================================= */

export type JaNein = "ja" | "nein" | "";
export type JaNeinWeiss = "ja" | "nein" | "weiss_nicht" | "";

export type AdvisorId = "franklin" | "aaron" | "alvaro" | "unbekannt" | "";

export interface VorsorgeVertrag {
  id: string;
  vertragsart: string; // Pflicht
  anbieter: string; // Pflicht (oder "weiss nicht")
  anbieterUnbekannt: boolean;
  beitrag: string; // Pflicht (oder "weiss nicht")
  beitragUnbekannt: boolean;
  produkt?: string;
  beginn?: string;
  ende?: string;
  hochrechnung?: string;
  vertragsnummer?: string;
}

export interface AkVertrag {
  id: string;
  vertragsart: string; // Berufsunfähigkeit / Grundfähigkeit / Unfall / sonstige
  anbieter: string;
  anbieterUnbekannt: boolean;
  beitrag: string;
  beitragUnbekannt: boolean;
  leistung: string;
  leistungUnbekannt: boolean;
  beginn?: string;
  ende?: string;
  vertragsnummer?: string;
}

export interface SachVertrag {
  id: string;
  art: string; // entspricht einer gewählten Versicherungsart
  anbieter: string;
  anbieterUnbekannt: boolean;
  beitrag: string;
  intervall: "mensual" | "anual" | "";
  beginn?: string;
  ende?: string;
  leistung?: string;
  vertragsnummer?: string;
}

export interface VermoegensPosten {
  id: string;
  kategorie: string;
  wert: string;
  wertUnbekannt: boolean;
  sparbeitrag?: string;
  anbieter?: string;
  notiz?: string;
}

export type UploadStatus = "pending" | "uploading" | "done" | "error";
export interface UploadMeta {
  id: string;
  kategorie: string;
  originalName: string;
  safeName: string;
  mime: string;
  size: number;
  status: UploadStatus;
  progress: number;
  driveFileId?: string;
  uploadedAt?: string;
}

export interface Analyse {
  meta: { vorgangsnummer: string; startedAt: string; consentAt: string };

  ziele: {
    beruehrungspunkte: string[];
    wichtig: string[];
    ziele: string[];
    zieleFreitext: string;
  };

  stammdaten: {
    vorname: string;
    nachname: string;
    geburtsdatum: string;
    geburtsort: string;
    staatsangehoerigkeit: string;
    strasse: string;
    hausnummer: string;
    plz: string;
    ort: string;
    email: string;
    mobil: string;
    familienstand: string;
    hatKinder: JaNein;
    anzahlKinder: string;
    alterKinder: string;
    berufsstatus: string;
    berufsbezeichnung: string;
    abschluss: string;
  };

  gesundheit: {
    koerperlich: JaNein;
    psychisch: JaNein;
    raucher: JaNein;
    risikohobby: JaNein;
    risikohobbyText: string;
  };

  altersvorsorge: { vorhanden: JaNeinWeiss; vertraege: VorsorgeVertrag[] };
  arbeitskraft: { vorhanden: JaNeinWeiss; vertraege: AkVertrag[] };
  sachversicherungen: { auswahl: string[]; vertraege: SachVertrag[] };
  vermoegen: { vorhanden: JaNeinWeiss; posten: VermoegensPosten[] };

  strom: {
    eigenerVertrag: JaNeinWeiss;
    anbieter: string;
    abschlag: string;
    vertragsende: string;
    kuendigungsfrist: string;
  };
  gas: {
    eigenerVertrag: JaNeinWeiss;
    anbieter: string;
    abschlag: string;
    vertragsende: string;
    kuendigungsfrist: string;
  };

  krankenversicherung: {
    gesetzlichVersichert: JaNein; // Gate: gesetzlich vs. privat/familienversichert
    anbieter: string; // Pflicht, wenn gesetzlich
    bonusprogramme: JaNein;
    bonusprogrammeText: string;
    halt: string; // Pflicht, wenn gesetzlich – Bindung an aktuelle Kasse
  };

  einkommen: {
    bruttoMonat: string; // nur Info – NICHT summiert
    nettoMonat: string;
    gehaltszahlungen: string; // "12" | "13" | "14" | "andere"
    gehaltszahlungenAndere: string;
    nebeneinkuenfte: string;
    kindergeld: string;
    weitereEinnahmen: string;
  };
  einkommensnachweis: {
    art: "jahresendabrechnung" | "gehaltsabrechnung" | "keine" | "";
    begruendung: string;
  };

  ausgaben: {
    wohnen: string;
    lebenshaltung: string;
    mobilitaet: string;
    kreditraten: string;
    unterhalt: string;
    versicherungen: string;
    versZielText: string; // Pflicht, wenn Versicherungsbeiträge > Summe der oben erfassten Verträge
    abosVereine: string;
    sonstige: string;
    sparbeitraege: string;
    sparZielText: string; // Pflicht, wenn Sparbeiträge > Summe der oben erfassten Sparprodukte
  };

  zukunft: { mindestens: string; komfortabel: string; einmalig: string };

  verantwortung: "selbst" | "unterstuetzung" | "abgeben" | "";
  risiko: number; // 1..10
  flexibilitaet: number; // 1..10
  praeferenz:
    | "kurzfristige_verluste"
    | "geringe_ertraege"
    | "verfuegbarkeit"
    | "falsche_entscheidung"
    | "";

  dokumente: UploadMeta[];
  sonstiges: string;

  beraterId: AdvisorId;

  einwilligungen: {
    datenschutz: boolean;
    verarbeitung: boolean;
    uebermittlung: boolean;
    weiterleitung: boolean;
    driveSpeicherung: boolean;
    gesundheit: boolean;
    angabenGeprueft: boolean;
  };
}

export function createEmptyAnalyse(): Analyse {
  return {
    meta: { vorgangsnummer: "", startedAt: new Date().toISOString(), consentAt: "" },
    ziele: { beruehrungspunkte: [], wichtig: [], ziele: [], zieleFreitext: "" },
    stammdaten: {
      vorname: "", nachname: "", geburtsdatum: "", geburtsort: "",
      staatsangehoerigkeit: "", strasse: "", hausnummer: "", plz: "", ort: "",
      email: "", mobil: "", familienstand: "", hatKinder: "", anzahlKinder: "",
      alterKinder: "", berufsstatus: "", berufsbezeichnung: "", abschluss: "",
    },
    gesundheit: { koerperlich: "", psychisch: "", raucher: "", risikohobby: "", risikohobbyText: "" },
    altersvorsorge: { vorhanden: "", vertraege: [] },
    arbeitskraft: { vorhanden: "", vertraege: [] },
    sachversicherungen: { auswahl: [], vertraege: [] },
    vermoegen: { vorhanden: "", posten: [] },
    strom: { eigenerVertrag: "", anbieter: "", abschlag: "", vertragsende: "", kuendigungsfrist: "" },
    gas: { eigenerVertrag: "", anbieter: "", abschlag: "", vertragsende: "", kuendigungsfrist: "" },
    krankenversicherung: { gesetzlichVersichert: "", anbieter: "", bonusprogramme: "", bonusprogrammeText: "", halt: "" },
    einkommen: {
      bruttoMonat: "", nettoMonat: "", gehaltszahlungen: "12", gehaltszahlungenAndere: "",
      nebeneinkuenfte: "", kindergeld: "", weitereEinnahmen: "",
    },
    einkommensnachweis: { art: "", begruendung: "" },
    ausgaben: {
      wohnen: "", lebenshaltung: "", mobilitaet: "", kreditraten: "", unterhalt: "",
      versicherungen: "", versZielText: "", abosVereine: "", sonstige: "", sparbeitraege: "", sparZielText: "",
    },
    zukunft: { mindestens: "", komfortabel: "", einmalig: "" },
    verantwortung: "",
    risiko: 5,
    flexibilitaet: 5,
    praeferenz: "",
    dokumente: [],
    sonstiges: "",
    beraterId: "",
    einwilligungen: {
      datenschutz: false, verarbeitung: false, uebermittlung: false,
      weiterleitung: false, driveSpeicherung: false, gesundheit: false, angabenGeprueft: false,
    },
  };
}

/* ---------------- Optionslisten ---------------- */

export const OPT_BERUEHRUNGSPUNKTE = [
  "Sin conocimientos previos", "Seguros", "Previsión de jubilación", "Inversiones",
  "Ayudas y subvenciones", "Inmuebles", "Ya me han asesorado", "Otros",
];

export const OPT_WICHTIG = [
  "Explicaciones claras", "Transparencia", "Flexibilidad", "Seguridad",
  "Oportunidades de rentabilidad", "Atención personal", "Gestión digital",
  "Costos bajos", "Acompañamiento a largo plazo",
];

export const OPT_ZIELE = [
  "Seguridad financiera", "Construir patrimonio", "Mejorar mi previsión de jubilación",
  "Aprovechar ayudas y subvenciones", "Optimizar seguros", "Ahorrar en impuestos",
  "Financiar una vivienda", "Proteger a mi familia", "Ganar flexibilidad", "Otros",
];

export const OPT_FAMILIENSTAND = [
  "soltero/a", "casado/a", "pareja de hecho registrada",
  "divorciado/a", "viudo/a", "separado/a",
];

/** Der eine Status, bei dem keine Berufsbezeichnung verlangt werden kann. */
export const OHNE_TAETIGKEIT = "actualmente sin actividad laboral";

export const OPT_BERUFSSTATUS = [
  "empleado/a", "aprendiz / en formación", "estudiante", "autónomo/a",
  "profesional liberal", "funcionario/a", OHNE_TAETIGKEIT, "otros",
];

export const OPT_AV_ART = [
  "Seguro de pensión privado", "Pensión Riester", "Pensión Rürup / básica",
  "Plan de pensiones de empresa (bAV)", "Seguro de pensión vinculado a fondos",
  "Seguro de vida", "Otro",
];

export const OPT_AK_ART = [
  "Seguro de incapacidad laboral", "Seguro de capacidades básicas",
  "Seguro de accidentes", "Otra cobertura",
];

export const OPT_SACH = [
  "Responsabilidad civil privada", "Seguro de contenido del hogar", "Protección jurídica", "Seguro de auto",
  "Responsabilidad civil de mascotas", "Seguro de cirugía para mascotas", "Seguro de salud para mascotas",
  "Seguro de edificio", "Seguro de salud en el extranjero",
  "Seguro de salud complementario", "Seguro dental complementario", "Otro",
];
export const OPT_SACH_NONE = ["Ninguno", "No lo sé"];

export const OPT_VERMOEGEN = [
  "Cuenta corriente", "Cuenta de ahorro a la vista", "Libreta de ahorro", "Contrato de ahorro para vivienda (Bausparvertrag)", "Cartera de valores (Depot)", "Fondos de inversión",
  "Previsión de jubilación privada", "Previsión de jubilación de empresa", "Inmueble",
  "Efectivo", "Criptomonedas", "Participaciones", "Otros bienes patrimoniales",
];

// §14 Altersvergleich – Quelle laut PDF: Bundeszentrale für politische Bildung.
// ⚠ Vor Veröffentlichung auf Belastbarkeit prüfen (Brief §14). Bis dahin werden die
// konkreten Zahlen NICHT angezeigt (siehe SHOW_ALTERSVERGLEICH_WERTE). Erst nach
// Quellenprüfung auf true setzen.
export const SHOW_ALTERSVERGLEICH_WERTE = false;
export const ALTERSVERGLEICH: { range: string; wert: number }[] = [
  { range: "18 a 20", wert: 6463 },
  { range: "21 a 25", wert: 8934 },
  { range: "26 a 30", wert: 23928 },
  { range: "31 a 35", wert: 47489 },
  { range: "36 a 40", wert: 84732 },
  { range: "41 a 45", wert: 117533 },
  { range: "46 a 50", wert: 145681 },
];

export const ADVISORS: { id: AdvisorId; label: string }[] = [
  { id: "franklin", label: "Franklin Ramirez" },
  { id: "aaron", label: "Aaron Magiera" },
  { id: "alvaro", label: "Alvaro Tigerino" },
  { id: "unbekannt", label: "No lo sé ahora mismo" },
];

export const UPLOAD_KATEGORIEN = [
  "Einkommensnachweis", "Altersvorsorge", "Arbeitskraftabsicherung",
  "Sachversicherung", "Stromabrechnung", "Gasabrechnung",
  "Personalausweis Vorderseite", "Personalausweis Rückseite", "Sonstige relevante Unterlage",
];

/** Etiquetas de visualización para claves internas (las claves de backend NO cambian). */
export const KATEGORIE_LABEL: Record<string, string> = {
  "Einkommensnachweis": "Comprobante de ingresos",
  "Altersvorsorge": "Previsión de jubilación",
  "Arbeitskraftabsicherung": "Capacidad laboral",
  "Sachversicherung": "Seguro de bienes",
  "Stromabrechnung": "Factura de electricidad",
  "Gasabrechnung": "Factura de gas",
  "Personalausweis Vorderseite": "DNI (anverso)",
  "Personalausweis Rückseite": "DNI (reverso)",
  "Sonstige relevante Unterlage": "Otro documento",
  "Mehrwertanalyse": "Análisis de Valor",
};
export function katLabel(k: string): string {
  return KATEGORIE_LABEL[k] || k;
}

/** Verantwortungs-Auswahl → Anzeige-Label (Schlüssel bleiben selbst/unterstuetzung/abgeben). */
export const VERANTWORTUNG_LABEL: Record<string, string> = {
  selbst: "Decido yo mismo/a",
  unterstuetzung: "Quiero apoyo",
  abgeben: "Quiero delegar la responsabilidad",
};

export const ALLOWED_MIME = [
  "application/pdf", "image/jpeg", "image/jpg", "image/png", "image/heic", "image/heif",
];
export const ALLOWED_EXT = ["pdf", "jpg", "jpeg", "png", "heic", "heif"];
export const MAX_FILE_BYTES = 15 * 1024 * 1024;
